import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import type { CardSkin } from '@lib/types';
import { cn, cva, type VariantProps } from '@lib/utils';
import { useThemeContext } from '@lib/theme/contexts/ThemeContext';

const cardVariants = cva(['rounded-lg'], {
  variants: {
    skin: {
      none: '',
      bordered: ['border border-line', 'print:border-0'],
      shadow: ['bg-surface', 'shadow-soft', 'print:shadow-none'],
    },
  },
  defaultVariants: {
    skin: 'bordered',
  },
});

type CardProps = {
  skin?: CardSkin;
} & Omit<VariantProps<typeof cardVariants>, 'skin'> &
  ComponentPropsWithoutRef<'div'>;

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, skin, children, ...rest }, ref) => {
    const { cardSkin } = useThemeContext();
    const effectiveSkin = skin ?? cardSkin ?? 'bordered';

    return (
      <div ref={ref} className={cn(cardVariants({ skin: effectiveSkin }), className)} {...rest}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export { Card };
export type { CardProps };
